import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const adoptSession = useCallback((user) => setUser(user), []);

  const restoreSession = useCallback(async () => {
    try {
      const response = await api.get("/auth/session");
      if (response.status === 200) { setUser(response.data); return; }
    } catch { return; }
    const legacyToken = localStorage.getItem("biga_token");
    if (!legacyToken) return;
    try {
      const { data } = await api.post("/auth/migrate", {}, { headers: { Authorization: `Bearer ${legacyToken}` } });
      localStorage.removeItem("biga_token");
      setUser(data.user);
    } catch {
      localStorage.removeItem("biga_token");
    }
  }, []);

  useEffect(() => {
    if (window.location.hash.includes("session_id=")) { setLoading(false); return; }
    restoreSession().finally(() => setLoading(false));
  }, [restoreSession]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      adoptSession(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const register = async (name, email, password, birthday = {}) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password, ...birthday });
      adoptSession(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } finally { localStorage.removeItem("biga_token"); setUser(null); }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, adoptSession, restoreSession }}>
      {children}
    </AuthContext.Provider>
  );
}
